import React, { useState, useRef, useEffect } from "react";

import "../Styles/FormModal.css";
import BookingStatusModal from "./BookingStatusModal";

const FormModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      // Disable background scroll
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "IN",
    phone: "",
    date: "",
    message: "",
  });

  const [bookingStatus, setBookingStatus] = useState("idle");

  const [errors, setErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const dragStartPos = useRef({ x: 0, y: 0 });

  /* ---------- Validation helpers ---------- */
  const validateEmail = (email) => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!emailRegex.test(email)) return false;

    const domain = email.split("@")[1].toLowerCase();

    // Legit public providers (exact match only)
    const publicDomains = [
      "gmail.com",
      "outlook.com",
      "hotmail.com",
      "yahoo.com",
      "icloud.com",
      "live.com",
    ];

    // Block look-alike public domains (gmailllll, gmali, etc.)
    const looksLikePublic = publicDomains.some(
      (d) => domain.includes(d.replace(".com", "")) && domain !== d,
    );

    if (looksLikePublic) return false;

    // Block disposable emails
    const blockedDomains = [
      "mailinator.com",
      "tempmail.com",
      "10minutemail.com",
    ];

    if (blockedDomains.includes(domain)) return false;

    return true;
  };

  const validatePhone = (phone) => {
    const regex = /^[0-9]{10}$/;
    return regex.test(phone);
  };

  const validateName = (name) => {
    const nameRegex = /^[A-Za-z ]+$/;
    return nameRegex.test(name.trim());
  };

  /* ---------- Handle change ---------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({ ...prev, [name]: value }));

    if (name === "firstName") {
      setErrors((prev) => ({
        ...prev,
        firstName: validateName(value)
          ? ""
          : "First name should contain letters only",
      }));
    }

    if (name === "lastName") {
      setErrors((prev) => ({
        ...prev,
        lastName: validateName(value)
          ? ""
          : "Last name should contain letters only",
      }));
    }

    if (name === "email") {
      setErrors((prev) => ({
        ...prev,
        email: validateEmail(value)
          ? ""
          : "Please enter a valid business or personal email address",
      }));
    }

    if (name === "phone") {
      setErrors((prev) => ({
        ...prev,
        phone: validatePhone(value) ? "" : "Phone number must be 10 digits",
      }));
    }
  };

  /* ---------- Handle submit ---------- */
  const handleSubmit = (e) => {
    e.preventDefault();

    const firstNameValid = validateName(formData.firstName);
    const lastNameValid = validateName(formData.lastName);
    const emailValid = validateEmail(formData.email);
    const phoneValid = validatePhone(formData.phone);

    if (!firstNameValid || !lastNameValid || !emailValid || !phoneValid) {
      setErrors({
        firstName: firstNameValid
          ? ""
          : "First name should contain letters only",
        lastName: lastNameValid ? "" : "Last name should contain letters only",
        email: emailValid
          ? ""
          : "Please enter a valid business or personal email address",
        phone: phoneValid ? "" : "Phone number must be 10 digits",
      });
      return;
    }

    // 🔥 Open custom booking dialog immediately
    setBookingStatus("loading");

    fetch(`${process.env.REACT_APP_API_BASE_URL}/send-email`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(formData),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.success) {
          setBookingStatus("success");

          setFormData({
            firstName: "",
            lastName: "",
            email: "",
            countryCode: "IN",
            phone: "",
            date: "",
            message: "",
          });

          setErrors({
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
          });

          // ⏱ Auto close after success
          setTimeout(() => {
            setBookingStatus("idle");
            onClose();
          }, 10000); // ⏱ 10 seconds
        } else {
          setBookingStatus("error");
        }
      })
      .catch(() => {
        setBookingStatus("error");
      });
  };

  /* ---------- Drag logic ---------- */
  const onMouseDown = (e) => {
    dragging.current = true;
    dragStartPos.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  const onMouseMove = (e) => {
    if (!dragging.current) return;
    setPosition({
      x: e.clientX - dragStartPos.current.x,
      y: e.clientY - dragStartPos.current.y,
    });
  };

  const onMouseUp = () => {
    dragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translate(${position.x}px, ${position.y}px)`,
          cursor: dragging.current ? "grabbing" : "default",
        }}
      >
        <div
          className="modal-header"
          onMouseDown={onMouseDown}
          style={{ cursor: "grab", userSelect: "none" }}
        >
          <h2>Book an Appointment</h2>
          <button className="close-button" onClick={onClose}>
            &times;
          </button>
        </div>

        <div className="booking-container">
          <form
            onSubmit={handleSubmit}
            className="booking-form"
            autoComplete="off"
          >
            <div className="name-fields">
              <div>
                <label>First name</label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                />
                {errors.firstName && (
                  <span className="error-text">{errors.firstName}</span>
                )}
              </div>

              <div>
                <label>Last name</label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                />
                {errors.lastName && (
                  <span className="error-text">{errors.lastName}</span>
                )}
              </div>
            </div>

            <div>
              <label>Email address</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
              {errors.email && (
                <span className="error-text">{errors.email}</span>
              )}
            </div>

            <div className="phone-select">
              <div>
                <label>Country</label>
                <select
                  name="countryCode"
                  value={formData.countryCode}
                  onChange={handleChange}
                >
                  <option value="IN">IN</option>
                </select>
              </div>

              <div>
                <label>Phone number</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
                {errors.phone && (
                  <span className="error-text">{errors.phone}</span>
                )}
              </div>
            </div>

            <div>
              <label>Select date for appointment</label>
              <input
                type="date"
                name="date"
                min={new Date().toISOString().split("T")[0]}
                value={formData.date}
                onChange={handleChange}
                required
              />
            </div>

            <div>
              <label>Message</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
              />
            </div>

            <button type="submit" disabled={bookingStatus === "loading"}>
              {bookingStatus === "loading"
                ? "Booking..."
                : "Book an appointment"}
            </button>
          </form>
        </div>
      </div>
      {bookingStatus !== "idle" && (
        <BookingStatusModal
          status={bookingStatus} // 👈 THIS LINE
          onClose={() => setBookingStatus("idle")}
        />
      )}
    </div>
  );
};
// This is Form Modal
export default FormModal;
